'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import { executeWasteBatch } from '@/features/kitchen/api/executeWaste'
import { uploadPhoto } from '@/shared/api/uploadPhoto'
import { fetchTodayWasteBatches } from '@/features/kitchen/api/getTodayWaste'
import { fetchKitchenStockLots, fetchKitchenWasteStockLots } from '@/features/kitchen/api/getDispatchOptions'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { mapWasteBatchesToUi } from '@/features/kitchen/api/mapWasteFromApi'
import { submitWasteProposal } from '@/features/kitchen/api/submitWasteProposal'
import { fetchTodayProductionPlan } from '@/features/kitchen/api/getTodayProduction'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import {
  buildPlanCatalogFromTodayPlan,
  groupProductLinesByCategory,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { kitchenTodayCompact } from '@/features/kitchen/lib/kitchenDates'
import { loadWasteBatches, saveWasteBatches } from '@/features/kitchen/storage/wasteStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import {
  buildCatalogFromStockItems,
  getProposalChecklist,
  groupAddItemOptionsByCategory,
  groupWasteItemsForDisplay,
  mergeProductCatalogs,
  sortWasteProductOptions,
  type WasteDisplayGroup,
} from '@/features/kitchen/wasteProposalUi'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type WasteStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'done'

type StockSource = 'main' | 'return' | 'damaged'

type WasteItem = {
  id: string
  productName: string
  productionDate: string
  stockSource: StockSource
  qty: number
  reason?: string
}

type WasteBatch = {
  id: string
  batchId?: string
  status: WasteStatus
  items: WasteItem[]
  proposalNote: string
  executionNote: string
  photos: string[]
  updatedAt: string
  submittedAt?: string
  approvedAt?: string
  executedAt?: string
  rejectionReason?: string
}

type StockItem = {
  productName: string
  productionDate: string
  source: StockSource
  stockAvailable: number
  categoryName?: string
  categorySortOrder?: number
}

function getWasteStockSources(): {
  key: StockSource
  label: string
  shortLabel: string
  hint: string
  icon: string
  chipClass: string
}[] {
  const s = kitchenTerms.waste.stockSources
  return [
    {
      key: 'main',
      label: s.main.label,
      shortLabel: s.main.shortLabel,
      hint: s.main.hint,
      icon: 'inventory_2',
      chipClass: 'bg-surface-container text-on-surface-variant border border-outline-variant',
    },
    {
      key: 'return',
      label: s.return.label,
      shortLabel: s.return.shortLabel,
      hint: s.return.hint,
      icon: 'delete_sweep',
      chipClass: 'bg-secondary-fixed text-on-secondary-fixed-variant border border-secondary-fixed-dim',
    },
  ]
}

const MOCK_WASTE_PLAN_CATALOG: PlanCatalogItem[] = [
  { sku: 'Roti coklat', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Roti keju', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat gula', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Croissant', plan: 0, categoryName: 'Pastry', categorySortOrder: 2 },
]

const BASE_STOCK: StockItem[] = [
  { productName: 'Roti coklat', productionDate: '23/06/26', source: 'main', stockAvailable: 70 },
  { productName: 'Roti keju', productionDate: '23/06/26', source: 'main', stockAvailable: 40 },
  { productName: 'Donat gula', productionDate: '23/06/26', source: 'main', stockAvailable: 10 },
  { productName: 'Croissant', productionDate: '23/06/26', source: 'main', stockAvailable: 12 },
  { productName: 'Donat gula', productionDate: '23/06/26', source: 'return', stockAvailable: 15 },
  { productName: 'Roti coklat', productionDate: '22/06/26', source: 'return', stockAvailable: 8 },
  { productName: 'Croissant', productionDate: '22/06/26', source: 'return', stockAvailable: 6 },
  { productName: 'Roti coklat', productionDate: '22/06/26', source: 'damaged', stockAvailable: 5 },
  { productName: 'Roti keju', productionDate: '20/06/26', source: 'damaged', stockAvailable: 6 },
]

type View = 'list' | 'proposal' | 'execute' | 'detail'
type ListFilter = 'draft' | 'submitted' | 'approved' | 'done'
type EditorStep = 'edit' | 'review'
type PinMode = 'submit' | 'execute'
type ConfirmAction =
  | { type: 'deleteItem'; itemId: string; label: string }
  | { type: 'deleteBatch'; batchId: string }

const INITIAL_BATCHES: WasteBatch[] = [
  {
    id: 'DSP-KIT-20260623-01',
    status: 'draft',
    updatedAt: 'Baru dibuat',
    proposalNote: 'Sisa closing kemarin',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w1',
        productName: 'Roti coklat',
        productionDate: '22/06/26',
        stockSource: 'damaged',
        qty: 5,
      },
      {
        id: 'w2',
        productName: 'Donat gula',
        productionDate: '23/06/26',
        stockSource: 'return',
        qty: 2,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260622-02',
    status: 'submitted',
    updatedAt: 'Dikirim 22/06 15:20',
    submittedAt: '22/06 15:20',
    proposalNote: 'Batch rusak oven',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w3',
        productName: 'Croissant',
        productionDate: '22/06/26',
        stockSource: 'damaged',
        qty: 8,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260621-03',
    status: 'approved',
    updatedAt: 'Disetujui HO 21/06 10:00',
    submittedAt: '20/06 16:30',
    approvedAt: '21/06 10:00',
    proposalNote: 'Expired storage',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w4',
        productName: 'Roti keju',
        productionDate: '20/06/26',
        stockSource: 'damaged',
        qty: 6,
      },
      {
        id: 'w5',
        productName: 'Roti coklat',
        productionDate: '20/06/26',
        stockSource: 'main',
        qty: 4,
        reason: 'Kadaluarsa hari ini',
      },
    ],
  },
  {
    id: 'DSP-KIT-20260620-05',
    status: 'rejected',
    updatedAt: 'Ditolak HO 20/06 09:15',
    submittedAt: '19/06 17:00',
    rejectionReason: 'Qty donat melebihi stok retur tercatat. Perbaiki jumlah lalu kirim ulang.',
    proposalNote: 'Retur toko kemarin',
    executionNote: '',
    photos: [],
    items: [
      {
        id: 'w7',
        productName: 'Donat gula',
        productionDate: '23/06/26',
        stockSource: 'return',
        qty: 20,
      },
    ],
  },
  {
    id: 'DSP-KIT-20260618-04',
    status: 'done',
    updatedAt: 'Selesai 18/06 11:45',
    submittedAt: '17/06 14:00',
    approvedAt: '18/06 08:00',
    executedAt: '18/06 11:45',
    proposalNote: 'Sisa closing',
    executionNote: 'Sudah dibuang ke tempat sampah dapur',
    photos: ['mock-photo'],
    items: [
      {
        id: 'w6',
        productName: 'Donat gula',
        productionDate: '17/06/26',
        stockSource: 'main',
        qty: 10,
        reason: 'Sisa closing tidak laku',
      },
    ],
  },
]

function productBucketKey(source: StockSource, productName: string): string {
  return `${source}|${productName}`
}

function buildProductStockTotals(baseStock: StockItem[]): Map<string, number> {
  const totals = new Map<string, number>()
  for (const item of baseStock) {
    const key = productBucketKey(item.source, item.productName)
    totals.set(key, (totals.get(key) ?? 0) + item.stockAvailable)
  }
  return totals
}

type AggregatedProductOption = {
  productName: string
  source: StockSource
  available: number
}

function aggregateProductOptions(
  stockItems: StockItem[],
  source: StockSource,
  remainingProductStock: Map<string, number>,
  excludeProducts: Set<string>,
): AggregatedProductOption[] {
  const seen = new Set<string>()
  const out: AggregatedProductOption[] = []
  for (const item of stockItems) {
    if (item.source !== source || seen.has(item.productName)) continue
    seen.add(item.productName)
    if (excludeProducts.has(item.productName)) continue
    const available = remainingProductStock.get(productBucketKey(source, item.productName)) ?? 0
    if (available > 0) {
      out.push({ productName: item.productName, source, available })
    }
  }
  return out.sort((a, b) => a.productName.localeCompare(b.productName, 'id'))
}

function isGoodStockSource(source: StockSource): boolean {
  return source === 'main'
}

function normalizeStockSource(source: StockSource): 'main' | 'return' {
  return source === 'main' ? 'main' : 'return'
}

function stockSourceLabel(source: StockSource): string {
  return stockSourceMeta(source).label
}

function statusLabel(status: WasteStatus): string {
  const t = kitchenTerms.waste.status
  if (status === 'draft') return t.draft
  if (status === 'submitted') return t.submitted
  if (status === 'approved') return t.approved
  if (status === 'rejected') return t.rejected
  return t.done
}

function statusBadge(status: WasteStatus): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'submitted') return 'badge-warning'
  if (status === 'approved') return 'badge-success'
  if (status === 'rejected') return 'badge-danger'
  return 'badge-success'
}

function formatNowLabel(prefix: string): string {
  const now = new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(now)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(now)
  return `${prefix} ${day} ${time}`
}

function getFilledItems(items: WasteItem[]): WasteItem[] {
  return items.filter((item) => item.qty > 0 && item.productName.trim())
}

function nextBatchId(batches: WasteBatch[]): string {
  const dateRef = kitchenTodayCompact()
  const count = batches.filter((b) => b.id.includes(dateRef)).length + 1
  return `DSP-KIT-${dateRef}-${String(count).padStart(2, '0')}`
}

function reservesStock(status: WasteStatus): boolean {
  return status !== 'rejected'
}

function computeReservedStock(
  batches: WasteBatch[],
  excludeBatchId?: string | null,
  baseStock: StockItem[] = BASE_STOCK,
): Map<string, number> {
  const remaining = buildProductStockTotals(baseStock)

  for (const batch of batches) {
    if (batch.id === excludeBatchId || !reservesStock(batch.status)) continue
    if (batch.status === 'done') continue
    for (const item of getFilledItems(batch.items)) {
      const key = productBucketKey(item.stockSource, item.productName)
      remaining.set(key, (remaining.get(key) ?? 0) - item.qty)
    }
  }

  for (const batch of batches) {
    if (batch.status !== 'done') continue
    for (const item of getFilledItems(batch.items)) {
      const key = productBucketKey(item.stockSource, item.productName)
      remaining.set(key, (remaining.get(key) ?? 0) - item.qty)
    }
  }

  return remaining
}

function getItemStockLimit(
  batches: WasteBatch[],
  batchId: string,
  source: StockSource,
  productName: string,
  baseStock: StockItem[] = BASE_STOCK,
): number {
  const remaining = computeReservedStock(batches, batchId, baseStock)
  const key = productBucketKey(source, productName)
  return Math.max(0, remaining.get(key) ?? 0)
}

function stockSourceMeta(source: StockSource) {
  const key = normalizeStockSource(source)
  return getWasteStockSources().find((s) => s.key === key) ?? getWasteStockSources()[0]
}

function summarizeQtyBySource(items: WasteItem[]): Record<StockSource, number> {
  const totals: Record<StockSource, number> = { main: 0, return: 0, damaged: 0 }
  for (const item of getFilledItems(items)) {
    totals[item.stockSource] += item.qty
  }
  return totals
}

function formatSourceBreakdown(items: WasteItem[]): string {
  const totals = summarizeQtyBySource(items)
  return kitchenTerms.waste.card.sourceBreakdown(
    getWasteStockSources()
      .filter((s) => totals[s.key] > 0)
      .map((s) => `${s.shortLabel} ${totals[s.key]}`),
  )
}

function computeAvailableBySource(
  remainingStock: Map<string, number>,
): Record<StockSource, number> {
  const totals: Record<StockSource, number> = { main: 0, return: 0, damaged: 0 }
  for (const [key, qty] of remainingStock) {
    const source = key.split('|')[0] as StockSource
    if (source in totals) {
      totals[source] += Math.max(0, qty)
    }
  }
  return totals
}

function wasteFlowActiveStep(status: WasteStatus): number {
  if (status === 'done') return 5
  if (status === 'approved') return 3
  if (status === 'submitted' || status === 'rejected') return 2
  return 1
}

const WASTE_FLOW_STEPS = kitchenTerms.waste.flowSteps.map((label) => ({ label }))

function StockSourceChip({ source, compact = false }: { source: StockSource; compact?: boolean }) {
  const meta = stockSourceMeta(source)
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-label-md font-semibold ${meta.chipClass} ${
        compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2.5 py-1'
      }`}
    >
      <span className="material-symbols-outlined text-sm leading-none">{meta.icon}</span>
      {compact ? meta.shortLabel : meta.label}
    </span>
  )
}

function ItemReasonBlock({ reason }: { reason?: string }) {
  if (!reason?.trim()) return null
  return (
    <div className="rounded-lg bg-surface-container px-3 py-2">
      <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.editor.reasonTitle}</p>
      <p className="text-body-sm text-on-surface mt-0.5">{reason}</p>
    </div>
  )
}

function StockStagesBanner() {
  return (
    <div className="mt-3 pt-3 border-t border-surface-variant space-y-2">
      <p className="text-body-sm text-on-surface-variant">{kitchenTerms.waste.subtitleHint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {kitchenTerms.waste.stockStages.map((stage) => (
          <div
            key={stage.step}
            className="rounded-lg bg-surface-container border border-outline-variant px-3 py-2"
          >
            <p className="text-label-md font-semibold text-primary">
              {stage.step}. {stage.label}
            </p>
            <p className="text-body-sm text-on-surface-variant mt-0.5">{stage.detail}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProposalChecklistPanel({
  hasItems,
  reasonsOk,
  stockOk,
}: {
  hasItems: boolean
  reasonsOk: boolean
  stockOk: boolean
}) {
  const rows = [
    { ok: hasItems, label: kitchenTerms.waste.editor.checklistItems },
    { ok: reasonsOk, label: kitchenTerms.waste.editor.checklistReasons },
    { ok: stockOk, label: kitchenTerms.waste.editor.checklistStock },
  ]
  return (
    <div className="rounded-lg border border-outline-variant p-3 space-y-2">
      <p className="text-label-md font-semibold text-on-surface-variant">
        {kitchenTerms.waste.editor.checklistTitle}
      </p>
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-body-sm">
          <span
            className={`material-symbols-outlined ${row.ok ? 'text-status-success' : 'text-on-surface-variant'}`}
          >
            {row.ok ? 'check_circle' : 'radio_button_unchecked'}
          </span>
          <span className={row.ok ? 'text-on-surface' : 'text-on-surface-variant'}>{row.label}</span>
        </div>
      ))}
    </div>
  )
}

function WasteItemDisplayCard({
  group,
  showLotHint = false,
}: {
  group: WasteDisplayGroup
  showLotHint?: boolean
}) {
  return (
    <div className="rounded-lg border border-outline-variant p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2 min-w-0">
          <p className="text-body-lg font-semibold text-on-surface">{group.productName}</p>
          <StockSourceChip source={group.stockSource} compact />
        </div>
        <p className="text-body-lg font-bold text-primary shrink-0">
          {group.totalQty} {kitchenTerms.production.pcs}
        </p>
      </div>
      {group.showLotBreakdown ? (
        <div className="rounded-lg bg-surface-container px-3 py-2 space-y-1">
          <p className="text-label-md text-on-surface-variant">
            {kitchenTerms.waste.review.lotBreakdownTitle}
          </p>
          {group.lots.map((lot) => (
            <p key={`${lot.productionDate}-${lot.qty}`} className="text-body-sm text-on-surface">
              {kitchenTerms.waste.review.lotLine(lot.productionDate, lot.qty)}
            </p>
          ))}
        </div>
      ) : group.lots.length === 1 ? (
        <p className="text-body-sm text-on-surface-variant">
          {kitchenTerms.waste.review.lotLine(group.lots[0].productionDate, group.lots[0].qty)}
        </p>
      ) : null}
      {showLotHint && !group.showLotBreakdown && group.lots.length === 0 && (
        <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.detail.lotBreakdownHint}</p>
      )}
      {isGoodStockSource(group.stockSource) && <ItemReasonBlock reason={group.reason} />}
    </div>
  )
}

function WasteTimeline({ batch }: { batch: WasteBatch }) {
  const events = [
    batch.submittedAt
      ? { label: kitchenTerms.waste.timeline.submitted, at: batch.submittedAt, icon: 'send' }
      : null,
    batch.status === 'rejected' && batch.rejectionReason
      ? {
          label: kitchenTerms.waste.timeline.rejected,
          at: batch.updatedAt,
          icon: 'block',
          detail: batch.rejectionReason,
        }
      : batch.approvedAt
        ? { label: kitchenTerms.waste.timeline.approved, at: batch.approvedAt, icon: 'check_circle' }
        : null,
    batch.executedAt
      ? { label: kitchenTerms.waste.timeline.executed, at: batch.executedAt, icon: 'delete_sweep' }
      : null,
  ].filter(Boolean) as { label: string; at: string; icon: string; detail?: string }[]

  if (events.length === 0) return null

  return (
    <div className="rounded-lg border border-outline-variant p-3 space-y-0">
      <p className="text-label-md font-semibold text-on-surface-variant mb-3">
        {kitchenTerms.waste.timeline.title}
      </p>
      {events.map((event, index) => (
        <div key={`${event.label}-${index}`} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                event.label === kitchenTerms.waste.timeline.rejected
                  ? 'bg-error-container text-error'
                  : 'bg-primary-container text-on-primary-container'
              }`}
            >
              <span className="material-symbols-outlined text-base">{event.icon}</span>
            </div>
            {index < events.length - 1 && <div className="w-0.5 flex-1 bg-outline-variant my-1 min-h-4" />}
          </div>
          <div className={`pb-4 ${index === events.length - 1 ? 'pb-0' : ''}`}>
            <p className="text-body-sm font-semibold text-on-surface">{event.label}</p>
            <p className="text-label-md text-on-surface-variant mt-0.5">{event.at}</p>
            {event.detail && (
              <p className="text-body-sm text-error mt-1 bg-error-container/60 rounded-lg px-2 py-1.5">
                {event.detail}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-40 bg-inverse-surface/40 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-surface-card border border-outline-variant rounded-xl shadow-sm p-4 space-y-4">
        <div>
          <p className="text-headline-md text-primary">{title}</p>
          <p className="text-body-sm text-on-surface-variant mt-2">{message}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={onCancel}>
            {kitchenTerms.waste.confirm.cancel}
          </button>
          <button
            type="button"
            className="btn-danger flex-1"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function KitchenWastePage() {
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const [batches, setBatches] = useState<WasteBatch[]>(() =>
    useApi ? [] : loadWasteBatches(INITIAL_BATCHES),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [apiLoadError, setApiLoadError] = useState<string | null>(null)
  const [view, setView] = useState<View>('list')
  const [listFilter, setListFilter] = useState<ListFilter>('draft')
  const [editorStep, setEditorStep] = useState<EditorStep>('edit')
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)
  const [isAddItemOpen, setIsAddItemOpen] = useState(false)
  const [newItemSource, setNewItemSource] = useState<StockSource>('main')
  const [newItemProduct, setNewItemProduct] = useState('')
  const [newItemQty, setNewItemQty] = useState(1)
  const [addItemError, setAddItemError] = useState<string | null>(null)
  const [addItemSearch, setAddItemSearch] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [pinMode, setPinMode] = useState<PinMode | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [stockItems, setStockItems] = useState<StockItem[]>(useApi ? [] : BASE_STOCK)
  const [planCatalog, setPlanCatalog] = useState<PlanCatalogItem[]>(
    useApi ? [] : MOCK_WASTE_PLAN_CATALOG,
  )

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    fetchTodayProductionPlan(device.deviceId)
      .then((plan) => {
        if (cancelled) return
        setPlanCatalog(buildPlanCatalogFromTodayPlan(plan))
      })
      .catch(() => {
        if (!cancelled) setPlanCatalog([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (useApi) return
    saveWasteBatches(batches)
  }, [batches, useApi])

  const reloadWasteBatches = useCallback(() => {
    if (!useApi || !device?.deviceId) return Promise.resolve()
    setApiLoadError(null)
    return fetchTodayWasteBatches(device.deviceId)
      .then((data) => {
        setBatches(mapWasteBatchesToUi(data.batches))
      })
      .catch((err) => {
        setApiLoadError(
          err instanceof Error ? err.message : kitchenTerms.waste.errors.loadFailed,
        )
      })
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    reloadWasteBatches().finally(() => {
      if (!cancelled) setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, reloadWasteBatches])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    Promise.all([
      fetchKitchenStockLots(device.deviceId).catch(() => []),
      fetchKitchenWasteStockLots(device.deviceId).catch(() => []),
    ])
      .then(([good, waste]) => {
        if (cancelled) return
        const goodItems = good.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          source: 'main' as StockSource,
          stockAvailable: s.stockAvailable,
          categoryName: s.categoryName,
          categorySortOrder: s.categorySortOrder,
        }))
        const wasteItems = waste.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          source: 'return' as StockSource,
          stockAvailable: s.stockAvailable,
          categoryName: s.categoryName,
          categorySortOrder: s.categorySortOrder,
        }))
        setStockItems([...goodItems, ...wasteItems])
      })
      .catch(() => {
        if (!cancelled) setStockItems([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, batches])

  const kitchenName = device?.kitchenName ?? 'Dapur'

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  const selectedBatch = batches.find((b) => b.id === selectedBatchId) ?? null

  const draftBatches = useMemo(() => batches.filter((b) => b.status === 'draft'), [batches])
  const submittedBatches = useMemo(
    () => batches.filter((b) => b.status === 'submitted' || b.status === 'rejected'),
    [batches],
  )
  const approvedBatches = useMemo(() => batches.filter((b) => b.status === 'approved'), [batches])
  const doneBatches = useMemo(() => batches.filter((b) => b.status === 'done'), [batches])
  const rejectedCount = useMemo(
    () => batches.filter((b) => b.status === 'rejected').length,
    [batches],
  )

  const productCatalog = useMemo(
    () => mergeProductCatalogs(buildCatalogFromStockItems(stockItems), planCatalog),
    [stockItems, planCatalog],
  )

  const visibleBatches = useMemo(() => {
    if (listFilter === 'draft') return draftBatches
    if (listFilter === 'submitted') return submittedBatches
    if (listFilter === 'approved') return approvedBatches
    return doneBatches
  }, [listFilter, draftBatches, submittedBatches, approvedBatches, doneBatches])

  const remainingStock = useMemo(
    () => computeReservedStock(batches, selectedBatchId, stockItems),
    [batches, selectedBatchId, stockItems],
  )

  function openList(filter?: ListFilter) {
    setView('list')
    setSelectedBatchId(null)
    setEditorStep('edit')
    setSaveError(null)
    if (filter) setListFilter(filter)
  }

  function createBatch() {
    const id = nextBatchId(batches)
    const batch: WasteBatch = {
      id,
      status: 'draft',
      items: [],
      proposalNote: '',
      executionNote: '',
      photos: [],
      updatedAt: kitchenTerms.waste.created,
    }
    setBatches((prev) => [batch, ...prev])
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setListFilter('draft')
    showToast(kitchenTerms.waste.toast.created)
  }

  function openProposal(id: string) {
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setSaveError(null)
  }

  function openExecute(id: string) {
    setSelectedBatchId(id)
    setView('execute')
    setSaveError(null)
  }

  function openDetail(id: string) {
    setSelectedBatchId(id)
    setView('detail')
    setSaveError(null)
  }

  function updateBatch(patch: Partial<WasteBatch>) {
    if (!selectedBatchId) return
    setBatches((prev) => prev.map((b) => (b.id === selectedBatchId ? { ...b, ...patch } : b)))
  }

  function updateItem(itemId: string, patch: Partial<WasteItem>) {
    if (!selectedBatchId) return
    setBatches((prev) =>
      prev.map((b) => {
        if (b.id !== selectedBatchId) return b
        return {
          ...b,
          items: b.items.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
        }
      }),
    )
  }

  function requestRemoveItem(itemId: string, label: string) {
    setConfirmAction({ type: 'deleteItem', itemId, label })
  }

  function requestDeleteBatch(batchId: string) {
    setConfirmAction({ type: 'deleteBatch', batchId })
  }

  function handleConfirmAction() {
    if (!confirmAction) return
    if (confirmAction.type === 'deleteItem') {
      removeItem(confirmAction.itemId)
      showToast(kitchenTerms.waste.toast.itemRemoved)
    } else {
      deleteBatch(confirmAction.batchId)
    }
    setConfirmAction(null)
  }

  function recreateFromRejected(batchId: string) {
    const source = batches.find((b) => b.id === batchId)
    if (!source || source.status !== 'rejected') return
    const id = nextBatchId(batches)
    const batch: WasteBatch = {
      id,
      status: 'draft',
      items: source.items.map((item) => ({ ...item, id: `w-${Date.now()}-${item.id}` })),
      proposalNote: source.proposalNote,
      executionNote: '',
      photos: [],
      updatedAt: kitchenTerms.waste.createdFromRejection,
    }
    setBatches((prev) => [batch, ...prev])
    setSelectedBatchId(id)
    setEditorStep('edit')
    setView('proposal')
    setListFilter('draft')
    showToast(kitchenTerms.waste.toast.recreated)
  }

  function removeItem(itemId: string) {
    if (!selectedBatchId) return
    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatchId ? { ...b, items: b.items.filter((item) => item.id !== itemId) } : b,
      ),
    )
  }

  function selectSourceInModal(source: StockSource) {
    setNewItemSource(source)
    const exclude = new Set(
      selectedBatch?.items
        .filter((line) => line.stockSource === source)
        .map((line) => line.productName) ?? [],
    )
    const options = aggregateProductOptions(stockItems, source, remainingStock, exclude)
    setNewItemProduct(options[0] ? productBucketKey(source, options[0].productName) : '')
    setNewItemQty(1)
    setAddItemError(null)
  }

  function openAddItem() {
    const firstSource =
      getWasteStockSources().find((source) => {
        const options = aggregateProductOptions(stockItems, source.key, remainingStock, new Set())
        return options.length > 0
      })?.key ?? 'main'

    selectSourceInModal(firstSource)
    setAddItemSearch('')
    setIsAddItemOpen(true)
  }

  function confirmAddItem() {
    if (!selectedBatchId || !newItemProduct) {
      setAddItemError(kitchenTerms.waste.errors.pickProduct)
      return
    }
    const parts = newItemProduct.split('|')
    const stockSource = parts[0] as StockSource
    const productName = parts[1]
    if (!productName) {
      setAddItemError(kitchenTerms.waste.errors.pickProduct)
      return
    }

    const limit = getItemStockLimit(batches, selectedBatchId, stockSource, productName, stockItems)
    if (newItemQty <= 0) {
      setAddItemError(kitchenTerms.waste.errors.qtyPositive)
      return
    }
    if (newItemQty > limit) {
      setAddItemError(kitchenTerms.waste.errors.insufficientStock(limit))
      return
    }

    const duplicate = selectedBatch?.items.some(
      (item) => item.productName === productName && item.stockSource === stockSource,
    )
    if (duplicate) {
      setAddItemError(kitchenTerms.waste.errors.duplicateProduct)
      return
    }

    const item: WasteItem = {
      id: `w-${Date.now()}`,
      productName,
      productionDate: '',
      stockSource,
      qty: newItemQty,
    }
    setBatches((prev) =>
      prev.map((b) => (b.id === selectedBatchId ? { ...b, items: [...b.items, item] } : b)),
    )
    setIsAddItemOpen(false)
    setAddItemError(null)
  }

  function canDeleteBatch(batch: WasteBatch): boolean {
    return batch.status === 'draft' && getFilledItems(batch.items).length === 0
  }

  function deleteBatch(id: string) {
    const batch = batches.find((b) => b.id === id)
    if (!batch || !canDeleteBatch(batch)) return
    setBatches((prev) => prev.filter((b) => b.id !== id))
    if (selectedBatchId === id) openList('draft')
    showToast(kitchenTerms.waste.toast.batchDeleted)
  }

  function validateProposal(batchId: string, items: WasteItem[]): boolean {
    const filled = getFilledItems(items)
    if (filled.length === 0) {
      setSaveError(kitchenTerms.waste.errors.saveMinOne)
      return false
    }
    const over = filled.find((item) => {
      const limit = getItemStockLimit(
        batches,
        batchId,
        item.stockSource,
        item.productName,
        stockItems,
      )
      return item.qty > limit
    })
    if (over) {
      const limit = getItemStockLimit(
        batches,
        batchId,
        over.stockSource,
        over.productName,
        stockItems,
      )
      setSaveError(
        kitchenTerms.waste.errors.overStock(
          over.productName,
          stockSourceLabel(over.stockSource),
          limit,
        ),
      )
      return false
    }
    const missingReason = filled.find(
      (item) => isGoodStockSource(item.stockSource) && !item.reason?.trim(),
    )
    if (missingReason) {
      setSaveError(kitchenTerms.waste.errors.reasonRequired(missingReason.productName))
      return false
    }
    setSaveError(null)
    return true
  }

  function saveDraft() {
    if (!selectedBatch || selectedBatch.status !== 'draft') return
    if (!validateProposal(selectedBatch.id, selectedBatch.items)) return
    updateBatch({ updatedAt: formatNowLabel(kitchenTerms.waste.savedAt) })
    openList('draft')
    showToast(kitchenTerms.waste.toast.saved)
  }

  function openSubmitReview() {
    if (!selectedBatch || selectedBatch.status !== 'draft') return
    if (!validateProposal(selectedBatch.id, selectedBatch.items)) return
    updateBatch({ updatedAt: formatNowLabel('Disimpan') })
    setEditorStep('review')
    setSaveError(null)
  }

  async function confirmSubmitToHo(pin: string) {
    if (!selectedBatch || !device) {
      throw new Error(kitchenTerms.waste.errors.deviceRequired)
    }
    const filled = getFilledItems(selectedBatch.items)
    const result = await submitWasteProposal({
      batchId: selectedBatch.batchId ?? selectedBatch.id,
      deviceId: device.deviceId,
      pin,
      lines: filled.map((item) => ({
        productName: item.productName,
        productionDate: '',
        stockSource: normalizeStockSource(item.stockSource),
        qty: item.qty,
        reason: item.reason?.trim() || undefined,
      })),
      note: selectedBatch.proposalNote.trim() || undefined,
    })

    const label = formatNowLabel(kitchenTerms.waste.submittedAt)
    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatch.id
          ? {
              ...b,
              status: 'submitted' as const,
              updatedAt: label,
              submittedAt: new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(result.submitted_at)),
            }
          : b,
      ),
    )
    setPinMode(null)
    openList('submitted')
    showToast(kitchenTerms.waste.toast.submitted(consumeLastSyncQueued()))
  }

  function handlePhotosSelected(files: FileList | null) {
    if (!files || !selectedBatchId) return
    const urls = Array.from(files).map((file) => URL.createObjectURL(file))
    updateBatch({ photos: [...(selectedBatch?.photos ?? []), ...urls] })
  }

  function removePhoto(index: number) {
    if (!selectedBatch) return
    const next = selectedBatch.photos.filter((_, i) => i !== index)
    updateBatch({ photos: next })
  }

  function openExecutePin() {
    if (!selectedBatch) return
    if (!selectedBatch.executionNote.trim()) {
      setSaveError(kitchenTerms.waste.errors.executionNoteRequired)
      return
    }
    if (selectedBatch.photos.length < 1) {
      setSaveError(kitchenTerms.waste.errors.photoRequired)
      return
    }
    setSaveError(null)
    setPinMode('execute')
  }

  async function confirmExecute(pin: string) {
    if (!selectedBatch || !device) {
      throw new Error(kitchenTerms.waste.errors.deviceRequired)
    }
    const batchRef = selectedBatch.batchId ?? selectedBatch.id
    const uploaded = await Promise.all(
      selectedBatch.photos.map((photo) =>
        uploadPhoto(photo, 'disposal', { batchId: batchRef }),
      ),
    )
    const result = await executeWasteBatch({
      batchId: selectedBatch.batchId ?? selectedBatch.id,
      deviceId: device.deviceId,
      pin,
      executionNote: selectedBatch.executionNote.trim(),
      photoCount: uploaded.length,
      photoUrls: uploaded.map((u) => u.key),
    })

    setBatches((prev) =>
      prev.map((b) =>
        b.id === selectedBatch.id
          ? {
              ...b,
              status: 'done' as const,
              updatedAt: formatNowLabel('Selesai'),
              executedAt: new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(result.executed_at)),
            }
          : b,
      ),
    )
    setPinMode(null)
    openList('done')
    showToast(kitchenTerms.waste.toast.executed(consumeLastSyncQueued()))
  }

  const filledItems = selectedBatch ? getFilledItems(selectedBatch.items) : []
  const filledTotalQty = filledItems.reduce((sum, item) => sum + item.qty, 0)
  const isProposalEditable = selectedBatch?.status === 'draft'

  const stockOptionsForSource = useMemo(() => {
    const exclude = new Set(
      selectedBatch?.items
        .filter((line) => line.stockSource === newItemSource)
        .map((line) => line.productName) ?? [],
    )
    return sortWasteProductOptions(
      aggregateProductOptions(stockItems, newItemSource, remainingStock, exclude),
      newItemSource,
    )
  }, [newItemSource, remainingStock, selectedBatch, stockItems])

  const filteredAddItemOptions = useMemo(() => {
    const q = addItemSearch.trim().toLowerCase()
    if (!q) return stockOptionsForSource
    return stockOptionsForSource.filter((item) => item.productName.toLowerCase().includes(q))
  }, [stockOptionsForSource, addItemSearch])

  const groupedAddItemOptions = useMemo(
    () => groupAddItemOptionsByCategory(filteredAddItemOptions, productCatalog),
    [filteredAddItemOptions, productCatalog],
  )

  const availableBySource = useMemo(
    () => computeAvailableBySource(remainingStock),
    [remainingStock],
  )

  const listEmptyConfig: Record<ListFilter, { icon: string; title: string; description: string }> = {
    draft: {
      icon: 'edit_note',
      title: kitchenTerms.waste.empty.draft.title,
      description: kitchenTerms.waste.empty.draft.description,
    },
    submitted: {
      icon: 'hourglass_top',
      title: kitchenTerms.waste.empty.submitted.title,
      description: kitchenTerms.waste.empty.submitted.description,
    },
    approved: {
      icon: 'delete_sweep',
      title: kitchenTerms.waste.empty.approved.title,
      description: kitchenTerms.waste.empty.approved.description,
    },
    done: {
      icon: 'task_alt',
      title: kitchenTerms.waste.empty.done.title,
      description: kitchenTerms.waste.empty.done.description,
    },
  }

  const newItemLimit = useMemo(() => {
    if (!newItemProduct || !selectedBatchId) return 0
    const parts = newItemProduct.split('|')
    const stockSource = parts[0] as StockSource
    const productName = parts[1]
    if (!productName) return 0
    return getItemStockLimit(batches, selectedBatchId, stockSource, productName, stockItems)
  }, [newItemProduct, selectedBatchId, batches, stockItems])

  const executionReady = Boolean(
    selectedBatch?.executionNote.trim() && (selectedBatch?.photos.length ?? 0) >= 1,
  )

  const reviewTotalsBySource = useMemo(
    () => summarizeQtyBySource(filledItems),
    [filledItems],
  )

  const proposalItemGroups = useMemo(() => {
    if (!selectedBatch) return []
    return groupProductLinesByCategory(selectedBatch.items, productCatalog)
  }, [selectedBatch, productCatalog])

  const reviewItemGroups = useMemo(() => {
    return groupProductLinesByCategory(filledItems, productCatalog)
  }, [filledItems, productCatalog])

  const proposalStockOk = useMemo(() => {
    if (!selectedBatch) return true
    return !getFilledItems(selectedBatch.items).some((item) => {
      const limit = getItemStockLimit(
        batches,
        selectedBatch.id,
        item.stockSource,
        item.productName,
        stockItems,
      )
      return item.qty > limit
    })
  }, [selectedBatch, batches, stockItems])

  const proposalChecklist = useMemo(
    () => getProposalChecklist(filledItems, isGoodStockSource, proposalStockOk),
    [filledItems, proposalStockOk],
  )

  function renderProposalItemEditor(item: WasteItem) {
    if (!selectedBatch) return null
    const limit = getItemStockLimit(
      batches,
      selectedBatch.id,
      item.stockSource,
      item.productName,
      stockItems,
    )
    const over = isProposalEditable && item.qty > limit
    return (
      <div key={item.id} className="rounded-lg border border-outline-variant p-3 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-2">
            <p className="text-body-lg font-semibold text-on-surface">{item.productName}</p>
            <div className="flex flex-wrap items-center gap-2">
              <StockSourceChip source={item.stockSource} compact />
            </div>
          </div>
          {isProposalEditable && (
            <button
              type="button"
              onClick={() => requestRemoveItem(item.id, item.productName)}
              className="text-status-danger min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={kitchenTerms.waste.editor.removeItemAria}
            >
              <span className="material-symbols-outlined">delete</span>
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <QtyStepper
            value={item.qty}
            min={0}
            max={isProposalEditable ? limit : undefined}
            disabled={!isProposalEditable}
            onChange={(qty) => updateItem(item.id, { qty })}
          />
          <span className="text-label-md text-on-surface-variant">
            {kitchenTerms.waste.editor.stockLimit(limit)}
          </span>
        </div>
        {over && (
          <p className="text-label-md text-status-danger font-medium">
            {kitchenTerms.waste.editor.overStock(limit)}
          </p>
        )}
        {isProposalEditable && isGoodStockSource(item.stockSource) && (
          <div className="space-y-1">
            <label className="field-label" htmlFor={`reason_${item.id}`}>
              {kitchenTerms.waste.editor.reasonLabel}
            </label>
            <textarea
              id={`reason_${item.id}`}
              rows={2}
              value={item.reason ?? ''}
              onChange={(e) => updateItem(item.id, { reason: e.target.value })}
              placeholder={kitchenTerms.waste.editor.reasonPlaceholder}
              className="input-field resize-none"
            />
          </div>
        )}
        {!isProposalEditable && isGoodStockSource(item.stockSource) && (
          <ItemReasonBlock reason={item.reason} />
        )}
      </div>
    )
  }

  return (
    <section className="space-y-4">
      <div className="card">
        <div>
          <p className="text-headline-md text-primary">{kitchenTerms.waste.title}</p>
          <p className="text-body-sm text-on-surface-variant mt-1 capitalize">
            {kitchenName} · {today}
          </p>
        </div>
        <StockStagesBanner />
      </div>

      {view === 'list' && (
        <div className="space-y-3">
          {apiLoadError && (
            <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
              <span className="text-body-sm">{apiLoadError}</span>
              <button
                type="button"
                className="btn-outline min-h-[36px] text-body-sm"
                onClick={() => {
                  setIsLoading(true)
                  void reloadWasteBatches().finally(() => setIsLoading(false))
                }}
              >
                {kitchenTerms.waste.retry}
              </button>
            </div>
          )}
          {isLoading && <SkeletonList count={3} label={kitchenTerms.waste.loading} />}
          <div className="card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-surface-container px-3 py-2">
              <div>
                <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.wasteStockNow}</p>
                <p className="text-body-lg font-bold text-on-surface">
                  {availableBySource.return} {kitchenTerms.production.pcs}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {getWasteStockSources().map((source) => (
                  <span
                    key={source.key}
                    className="text-label-md text-on-surface-variant bg-surface-card border border-outline-variant rounded-lg px-2 py-1"
                  >
                    {source.shortLabel}: <strong>{availableBySource[source.key]} pcs</strong>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">{kitchenTerms.waste.todayBatches}</p>
              <button type="button" className="btn-primary" onClick={createBatch}>
                + {kitchenTerms.waste.newProposal}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'draft' as const, label: kitchenTerms.waste.filters.draft, count: draftBatches.length },
                {
                  key: 'submitted' as const,
                  label: kitchenTerms.waste.filters.submitted,
                  count: submittedBatches.length,
                },
                {
                  key: 'approved' as const,
                  label: kitchenTerms.waste.filters.approved,
                  count: approvedBatches.length,
                },
                { key: 'done' as const, label: kitchenTerms.waste.filters.done, count: doneBatches.length },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setListFilter(filter.key)}
                  className={`rounded-lg px-2 py-2.5 text-body-sm font-semibold leading-tight min-h-[44px] ${
                    listFilter === filter.key
                      ? 'bg-primary-container text-on-primary-container'
                      : 'bg-surface text-on-surface-variant border border-outline-variant'
                  } ${filter.key === 'approved' && filter.count > 0 && listFilter !== 'approved' ? 'ring-2 ring-status-success/40' : ''} ${filter.key === 'submitted' && rejectedCount > 0 && listFilter !== 'submitted' ? 'ring-2 ring-status-danger/40' : ''}`}
                >
                  {filter.label}
                  {filter.count > 0 && (
                    <span className="block text-xs opacity-80 mt-0.5">({filter.count})</span>
                  )}
                  {filter.key === 'submitted' && rejectedCount > 0 && (
                    <span className="block text-[10px] text-status-danger mt-0.5">
                      {rejectedCount} {kitchenTerms.waste.filters.rejectedBadge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {visibleBatches.map((batch) => {
            const totalQty = getFilledItems(batch.items).reduce((sum, item) => sum + item.qty, 0)
            const deletable = canDeleteBatch(batch)
            const sourceSummary = formatSourceBreakdown(batch.items)
            const isRejected = batch.status === 'rejected'
            return (
              <div
                key={batch.id}
                className={`card ${isRejected ? 'border-status-danger/40 bg-error-container/30' : ''} ${batch.status === 'approved' ? 'border-status-success/30' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-body-lg font-bold text-on-surface">
                      {batch.items.length > 0
                        ? kitchenTerms.waste.card.itemsSummary(batch.items.length, totalQty)
                        : kitchenTerms.waste.card.emptyItems}
                    </p>
                    {sourceSummary && (
                      <p className="text-label-md text-on-surface-variant mt-1">{sourceSummary}</p>
                    )}
                    <p className="font-label-md text-on-surface-variant mt-2">
                      {kitchenTerms.waste.card.batchIdLabel(batch.id)}
                    </p>
                    <p className="text-label-md text-on-surface-variant">{batch.updatedAt}</p>
                    {isRejected && batch.rejectionReason && (
                      <p className="text-body-sm text-error mt-2 bg-error-container/60 rounded-lg px-2 py-1.5 border border-error-container">
                        {batch.rejectionReason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-start gap-2 shrink-0">
                    <span className={statusBadge(batch.status)}>{statusLabel(batch.status)}</span>
                    {deletable && (
                      <button
                        type="button"
                        onClick={() => requestDeleteBatch(batch.id)}
                        className="text-status-danger p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        aria-label={kitchenTerms.waste.card.deleteAria}
                      >
                        <span className="material-symbols-outlined text-xl">delete</span>
                      </button>
                    )}
                  </div>
                </div>
                {batch.status === 'draft' && (
                  <button
                    type="button"
                    className="btn-primary w-full mt-3 min-h-[44px]"
                    onClick={() => openProposal(batch.id)}
                  >
                    {kitchenTerms.waste.card.continueEdit}
                  </button>
                )}
                {batch.status === 'approved' && (
                  <button
                    type="button"
                    className="btn-success w-full mt-3"
                    onClick={() => openExecute(batch.id)}
                  >
                    {kitchenTerms.waste.card.execute}
                  </button>
                )}
                {batch.status === 'submitted' && (
                  <button
                    type="button"
                    className="btn-outline w-full mt-3 min-h-[44px]"
                    onClick={() => openDetail(batch.id)}
                  >
                    {kitchenTerms.waste.card.viewDetail}
                  </button>
                )}
                {batch.status === 'rejected' && (
                  <div className="flex flex-col gap-2 mt-3">
                    <button
                      type="button"
                      className="btn-primary w-full min-h-[44px]"
                      onClick={() => recreateFromRejected(batch.id)}
                    >
                      {kitchenTerms.waste.card.recreate}
                    </button>
                    <button
                      type="button"
                      className="btn-outline w-full min-h-[44px]"
                      onClick={() => openDetail(batch.id)}
                    >
                      {kitchenTerms.waste.card.viewDetail}
                    </button>
                  </div>
                )}
                {batch.status === 'done' && (
                  <button
                    type="button"
                    className="btn-outline w-full mt-3 min-h-[44px]"
                    onClick={() => openDetail(batch.id)}
                  >
                    {kitchenTerms.waste.card.viewDetail}
                  </button>
                )}
              </div>
            )
          })}

          {!isLoading && visibleBatches.length === 0 && (
            <div className="card">
              <EmptyState
                icon={listEmptyConfig[listFilter].icon}
                title={listEmptyConfig[listFilter].title}
                description={listEmptyConfig[listFilter].description}
              />
              {listFilter === 'draft' && (
                <button type="button" className="btn-primary w-full mt-4" onClick={createBatch}>
                  + {kitchenTerms.waste.newProposal}
                </button>
              )}
            </div>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center">
            {kitchenTerms.waste.backHome}
          </Link>
        </div>
      )}

      {view === 'proposal' && selectedBatch && editorStep === 'edit' && (
        <div className="relative pb-28">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md text-on-surface-variant">{selectedBatch.id}</p>
                <p className="text-headline-md text-primary mt-1">{kitchenTerms.waste.proposal}</p>
                <p className="text-body-sm text-on-surface-variant">{statusLabel(selectedBatch.status)}</p>
              </div>
              <button type="button" className="btn-outline min-h-[44px]" onClick={() => openList('draft')}>
                {kitchenTerms.waste.back}
              </button>
            </div>

            <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

            {isProposalEditable && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <p className="text-label-md font-semibold text-on-surface-variant">
                  {kitchenTerms.waste.stockAvailableToday}
                </p>
                <div className="flex flex-wrap gap-2">
                  {getWasteStockSources().map((source) => (
                    <span
                      key={source.key}
                      className="text-body-sm text-on-surface bg-surface-card border border-outline-variant rounded-lg px-2.5 py-1.5"
                    >
                      {source.shortLabel}{' '}
                      <strong>{availableBySource[source.key]} pcs</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isProposalEditable && (
              <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
                {kitchenTerms.waste.editor.fifoHint}
              </p>
            )}

            {isProposalEditable && (
              <button
                type="button"
                className="w-full min-h-[44px] py-3 border border-dashed border-outline rounded-lg text-body-sm font-semibold text-on-surface-variant"
                onClick={openAddItem}
              >
                {kitchenTerms.waste.editor.addItem}
              </button>
            )}

            {selectedBatch.items.length === 0 && isProposalEditable && (
              <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
                {kitchenTerms.waste.editor.noItems}
              </div>
            )}

            <div className="space-y-2">
              {proposalItemGroups.map((group) => {
                const groupQty = group.items.reduce((sum, item) => sum + item.qty, 0)
                return (
                  <CollapsibleCategoryBlock
                    key={group.categoryName}
                    categoryName={group.categoryName}
                    itemCount={group.items.length}
                    defaultOpen
                    summary={`${groupQty} ${kitchenTerms.production.pcs}`}
                  >
                    <div className="space-y-2 p-2">
                      {group.items.map((item) => renderProposalItemEditor(item))}
                    </div>
                  </CollapsibleCategoryBlock>
                )
              })}
            </div>

            {isProposalEditable && (
              <ProposalChecklistPanel
                hasItems={proposalChecklist.hasItems}
                reasonsOk={proposalChecklist.reasonsOk}
                stockOk={proposalChecklist.stockOk}
              />
            )}

            <div className="space-y-2">
              <label className="field-label" htmlFor="proposal_note">
                {kitchenTerms.waste.editor.proposalNoteLabel}
              </label>
              <textarea
                id="proposal_note"
                rows={2}
                value={selectedBatch.proposalNote}
                onChange={(e) => updateBatch({ proposalNote: e.target.value })}
                disabled={!isProposalEditable}
                placeholder={kitchenTerms.waste.editor.proposalNotePlaceholder}
                className="input-field resize-none"
              />
            </div>

            {filledItems.length > 0 && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-body-sm text-on-surface-variant">
                    {kitchenTerms.waste.editor.totalLabel}
                  </span>
                  <span className="text-body-lg font-bold">
                    {kitchenTerms.waste.card.itemsSummary(filledItems.length, filledTotalQty)}
                  </span>
                </div>
                <p className="text-label-md text-on-surface-variant">{formatSourceBreakdown(filledItems)}</p>
              </div>
            )}

            {saveError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{saveError}</span>
              </div>
            )}
          </div>

          {isProposalEditable && (
            <StickyActionFooter>
              <button type="button" className="btn-outline w-full min-h-[44px]" onClick={saveDraft}>
                {kitchenTerms.waste.editor.saveDraft}
              </button>
              <button
                type="button"
                className="btn-primary w-full min-h-[44px] disabled:opacity-50"
                onClick={openSubmitReview}
                disabled={!proposalChecklist.ready}
              >
                {kitchenTerms.waste.editor.submitHo}
              </button>
              <p className="text-label-md text-on-surface-variant text-center">
                {kitchenTerms.waste.editor.footerHint}
              </p>
            </StickyActionFooter>
          )}
        </div>
      )}

      {view === 'proposal' && selectedBatch && editorStep === 'review' && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="text-headline-md text-primary">{kitchenTerms.waste.review.title}</p>
              <p className="text-body-sm text-on-surface-variant">{kitchenTerms.waste.review.subtitle}</p>
            </div>
            <button type="button" className="btn-outline min-h-[44px]" onClick={() => setEditorStep('edit')}>
              {kitchenTerms.waste.back}
            </button>
          </div>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep('draft')} />

          <div className="rounded-xl border-2 border-primary/30 bg-primary-container/20 p-4 space-y-2">
            <p className="text-label-md font-semibold text-on-surface-variant">
              {kitchenTerms.waste.review.summaryTitle}
            </p>
            <p className="text-headline-md text-primary">
              {kitchenTerms.waste.review.summaryQty(filledItems.length, filledTotalQty)}
            </p>
            <p className="text-body-sm text-on-surface-variant">{formatSourceBreakdown(filledItems)}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {getWasteStockSources().filter((s) => reviewTotalsBySource[s.key] > 0).map((source) => (
              <span
                key={source.key}
                className="text-body-sm bg-surface-container border border-outline-variant rounded-lg px-3 py-1.5"
              >
                {source.shortLabel}: <strong>{reviewTotalsBySource[source.key]} pcs</strong>
              </span>
            ))}
          </div>

          <div className="space-y-2">
            {reviewItemGroups.map((group) => {
              const groupQty = group.items.reduce((sum, item) => sum + item.qty, 0)
              return (
                <CollapsibleCategoryBlock
                  key={group.categoryName}
                  categoryName={group.categoryName}
                  itemCount={group.items.length}
                  defaultOpen
                  summary={`${groupQty} ${kitchenTerms.production.pcs}`}
                >
                  <div className="divide-y divide-surface-variant border-t border-outline-variant">
                    {group.items.map((item) => (
                      <div key={item.id} className="flex justify-between gap-3 p-3">
                        <div className="space-y-1.5">
                          <p className="text-body-lg font-semibold">{item.productName}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <StockSourceChip source={item.stockSource} compact />
                            {item.productionDate && (
                              <span className="text-body-sm text-on-surface-variant">
                                prod. {item.productionDate}
                              </span>
                            )}
                          </div>
                          {isGoodStockSource(item.stockSource) && <ItemReasonBlock reason={item.reason} />}
                        </div>
                        <p className="text-body-lg font-bold text-primary shrink-0">
                          {item.qty} {kitchenTerms.production.pcs}
                        </p>
                      </div>
                    ))}
                  </div>
                </CollapsibleCategoryBlock>
              )
            })}
          </div>

          {selectedBatch.proposalNote.trim() && (
            <div className="rounded-lg border border-outline-variant p-3">
              <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.review.noteLabel}</p>
              <p className="text-body-sm mt-1">{selectedBatch.proposalNote}</p>
            </div>
          )}

          <p className="text-body-sm text-status-warning bg-status-warning-bg/40 rounded-lg px-3 py-2">
            {kitchenTerms.waste.review.warning}
          </p>

          <button
            type="button"
            className="btn-primary w-full min-h-[44px]"
            onClick={() => setPinMode('submit')}
          >
            {kitchenTerms.waste.review.confirmPin}
          </button>
        </div>
      )}

      {view === 'execute' && selectedBatch && selectedBatch.status === 'approved' && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="text-headline-md text-primary">
                {kitchenTerms.waste.execute.title(selectedBatch.id)}
              </p>
              <p className="text-body-sm text-on-surface-variant">{kitchenTerms.waste.execute.subtitle}</p>
            </div>
            <span className={statusBadge(selectedBatch.status)}>{statusLabel(selectedBatch.status)}</span>
          </div>

          <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => openList('approved')}>
            {kitchenTerms.waste.backToList}
          </button>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

          <p className="text-body-sm text-status-warning bg-status-warning-bg/40 rounded-lg px-3 py-2">
            {kitchenTerms.waste.execute.warning}
          </p>

          <div className="space-y-2">
            {selectedBatch &&
              groupWasteItemsForDisplay(selectedBatch.items, true).map((group) => (
                <WasteItemDisplayCard key={group.key} group={group} />
              ))}
          </div>

          <div className="rounded-lg border border-outline-variant p-3 space-y-2">
            <p className="text-label-md font-semibold text-on-surface-variant">
              {kitchenTerms.waste.execute.checklistTitle}
            </p>
            <div className="flex items-center gap-2 text-body-sm">
              <span
                className={`material-symbols-outlined ${selectedBatch.executionNote.trim() ? 'text-status-success' : 'text-on-surface-variant'}`}
              >
                {selectedBatch.executionNote.trim() ? 'check_circle' : 'radio_button_unchecked'}
              </span>
              {kitchenTerms.waste.execute.noteFilled}
            </div>
            <div className="flex items-center gap-2 text-body-sm">
              <span
                className={`material-symbols-outlined ${selectedBatch.photos.length >= 1 ? 'text-status-success' : 'text-on-surface-variant'}`}
              >
                {selectedBatch.photos.length >= 1 ? 'check_circle' : 'radio_button_unchecked'}
              </span>
              {kitchenTerms.waste.execute.photoFilled(selectedBatch.photos.length)}
            </div>
          </div>

          <div className="space-y-2">
            <label className="field-label" htmlFor="execution_note">
              {kitchenTerms.waste.execute.noteLabel}
            </label>
            <textarea
              id="execution_note"
              rows={2}
              value={selectedBatch.executionNote}
              onChange={(e) => updateBatch({ executionNote: e.target.value })}
              placeholder={kitchenTerms.waste.execute.notePlaceholder}
              className="input-field resize-none"
            />
          </div>

          <div className="space-y-2">
            <p className="field-label">{kitchenTerms.waste.execute.photoLabel}</p>
            <label className="btn-outline w-full min-h-[44px] inline-flex items-center justify-center gap-2 cursor-pointer">
              <span className="material-symbols-outlined">photo_camera</span>
              {selectedBatch.photos.length > 0
                ? kitchenTerms.waste.execute.addMorePhoto
                : kitchenTerms.waste.execute.addPhoto}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="sr-only"
                onChange={(e) => handlePhotosSelected(e.target.files)}
              />
            </label>
            {selectedBatch.photos.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedBatch.photos.map((src, index) => (
                  <div
                    key={`${src}-${index}`}
                    className="relative w-20 h-20 rounded-lg overflow-hidden border border-outline-variant bg-surface-container flex items-center justify-center"
                  >
                    {src.startsWith('blob:') || src.startsWith('http') ? (
                      <img
                        src={src}
                        alt={kitchenTerms.waste.execute.photoAlt(index + 1)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="material-symbols-outlined text-3xl text-on-surface-variant">
                        image
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-0 right-0 bg-inverse-surface/70 text-white p-0.5"
                      aria-label={kitchenTerms.waste.execute.removePhotoAria}
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {saveError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{saveError}</span>
            </div>
          )}

          <button
            type="button"
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
            disabled={!executionReady}
            onClick={openExecutePin}
          >
            {kitchenTerms.waste.execute.finish}
          </button>
        </div>
      )}

      {view === 'detail' && selectedBatch && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
            <div>
              <p className="font-label-md text-on-surface-variant">{selectedBatch.id}</p>
              <p className="text-headline-md text-primary mt-1">{kitchenTerms.waste.detail.title}</p>
              <span className={`${statusBadge(selectedBatch.status)} mt-2 inline-block`}>
                {statusLabel(selectedBatch.status)}
              </span>
            </div>
            <button
              type="button"
              className="btn-outline min-h-[44px]"
              onClick={() =>
                openList(
                  selectedBatch.status === 'draft'
                    ? 'draft'
                    : selectedBatch.status === 'approved'
                      ? 'approved'
                      : selectedBatch.status === 'done'
                        ? 'done'
                        : 'submitted',
                )
              }
            >
              {kitchenTerms.waste.back}
            </button>
          </div>

          <FlowStepper steps={[...WASTE_FLOW_STEPS]} activeStep={wasteFlowActiveStep(selectedBatch.status)} />

          {selectedBatch.status === 'rejected' && selectedBatch.rejectionReason && (
            <div className="rounded-lg border border-status-danger/30 bg-error-container/60 p-3 text-body-sm text-error">
              <p className="font-semibold">{kitchenTerms.waste.detail.rejectionTitle}</p>
              <p className="mt-1">{selectedBatch.rejectionReason}</p>
            </div>
          )}

          <div className="space-y-2">
            {selectedBatch &&
              groupWasteItemsForDisplay(selectedBatch.items, selectedBatch.status !== 'draft').map(
                (group) => (
                  <WasteItemDisplayCard
                    key={group.key}
                    group={group}
                    showLotHint={selectedBatch.status === 'submitted'}
                  />
                ),
              )}
          </div>

          {selectedBatch.proposalNote && (
            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.detail.proposalNote}</p>
              <p className="mt-1">{selectedBatch.proposalNote}</p>
            </div>
          )}

          {selectedBatch.executionNote && (
            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-label-md text-on-surface-variant">{kitchenTerms.waste.detail.executionNote}</p>
              <p className="mt-1">{selectedBatch.executionNote}</p>
            </div>
          )}

          {selectedBatch.photos.length > 0 && (
            <div className="space-y-2">
              <p className="text-label-md font-semibold text-on-surface-variant">{kitchenTerms.waste.detail.photos}</p>
              <div className="flex flex-wrap gap-2">
                {selectedBatch.photos.map((src, index) => (
                  <div
                    key={`${src}-${index}`}
                    className="w-20 h-20 rounded-lg overflow-hidden border border-outline-variant bg-surface-container flex items-center justify-center"
                  >
                    {src.startsWith('blob:') || src.startsWith('http') ? (
                      <img
                        src={src}
                        alt={kitchenTerms.waste.execute.photoAlt(index + 1)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="material-symbols-outlined text-3xl text-on-surface-variant">
                        image
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <WasteTimeline batch={selectedBatch} />

          {selectedBatch.status === 'rejected' && (
            <button
              type="button"
              className="btn-primary w-full min-h-[44px]"
              onClick={() => recreateFromRejected(selectedBatch.id)}
            >
              {kitchenTerms.waste.card.recreate}
            </button>
          )}
        </div>
      )}

      {isAddItemOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="w-full max-w-md max-h-[90vh] min-h-0 flex flex-col bg-surface-card border border-outline-variant rounded-t-xl sm:rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex justify-between items-center shrink-0">
              <p className="text-headline-md text-primary">{kitchenTerms.waste.addItem.title}</p>
              <button
                type="button"
                onClick={() => setIsAddItemOpen(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={kitchenTerms.waste.addItem.closeAria}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-2">
                <p className="field-label">{kitchenTerms.waste.addItem.sourceLabel}</p>
                <div className="grid grid-cols-2 gap-2">
                  {getWasteStockSources().map((source) => (
                    <button
                      key={source.key}
                      type="button"
                      onClick={() => selectSourceInModal(source.key)}
                      className={`rounded-lg px-2 py-2.5 text-body-sm font-semibold leading-tight min-h-[44px] ${
                        newItemSource === source.key
                          ? 'bg-primary-container text-on-primary-container'
                          : 'bg-surface text-on-surface-variant border border-outline-variant'
                      }`}
                    >
                      {source.shortLabel}
                    </button>
                  ))}
                </div>
                <p className="text-label-md text-on-surface-variant">
                  {stockSourceMeta(newItemSource).hint}
                </p>
              </div>

              <div className="space-y-2">
                <p className="field-label">{kitchenTerms.waste.addItem.productLabel}</p>
                {stockOptionsForSource.length > 0 && (
                  <input
                    type="search"
                    value={addItemSearch}
                    onChange={(e) => setAddItemSearch(e.target.value)}
                    placeholder={kitchenTerms.waste.addItem.searchPlaceholder}
                    className="input-field"
                  />
                )}
                {stockOptionsForSource.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
                    {kitchenTerms.waste.addItem.noStock(stockSourceMeta(newItemSource).shortLabel)}
                  </div>
                ) : filteredAddItemOptions.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
                    {kitchenTerms.waste.addItem.noSearchResults}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-44 sm:max-h-56 overflow-y-auto">
                    {groupedAddItemOptions.map((group) => (
                      <div key={group.categoryName} className="space-y-2">
                        <p className="text-label-md font-semibold text-on-surface-variant sticky top-0 bg-surface-card py-1">
                          {group.categoryName}
                        </p>
                        {group.items.map((item) => {
                          const key = productBucketKey(item.source, item.productName)
                          const selected = newItemProduct === key
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => {
                                setNewItemProduct(key)
                                setNewItemQty(Math.min(newItemQty || 1, item.available))
                                setAddItemError(null)
                              }}
                              className={`w-full text-left rounded-lg border p-3 min-h-[44px] transition-colors ${
                                selected
                                  ? 'border-primary bg-primary-container/20'
                                  : 'border-outline-variant bg-surface-container-low hover:border-primary/50'
                              }`}
                            >
                              <p className="text-body-lg font-semibold text-on-surface">{item.productName}</p>
                              <p className="text-body-sm text-on-surface-variant mt-1">
                                {kitchenTerms.waste.addItem.productOption(item.available)}
                              </p>
                            </button>
                          )
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-surface-variant shrink-0 bg-surface-card pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-3">
              {newItemProduct && newItemLimit > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="field-label">{kitchenTerms.waste.addItem.qtyLabel}</p>
                    <button
                      type="button"
                      className="text-body-sm font-semibold text-primary"
                      onClick={() => setNewItemQty(newItemLimit)}
                    >
                      {kitchenTerms.waste.addItem.fillMax(newItemLimit)}
                    </button>
                  </div>
                  <QtyStepper
                    value={newItemQty}
                    min={1}
                    max={newItemLimit}
                    onChange={setNewItemQty}
                  />
                </div>
              )}

              {addItemError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{addItemError}</span>
                </div>
              )}

              {newItemProduct && newItemLimit > 0 && (
                <p className="text-body-sm text-on-surface-variant text-center">
                  {kitchenTerms.waste.addItem.preview(
                    newItemProduct.split('|')[1] ?? '',
                    newItemQty,
                    stockSourceMeta(newItemSource).shortLabel,
                  )}
                </p>
              )}
              <div className="flex gap-2">
              <button
                type="button"
                className="btn-outline flex-1 min-h-[44px]"
                onClick={() => setIsAddItemOpen(false)}
              >
                {kitchenTerms.waste.addItem.cancel}
              </button>
              <button
                type="button"
                className="btn-primary flex-1 min-h-[44px] disabled:opacity-50"
                disabled={!newItemProduct || newItemLimit <= 0}
                onClick={confirmAddItem}
              >
                {kitchenTerms.waste.addItem.confirm}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmDialog
          title={
            confirmAction.type === 'deleteItem'
              ? kitchenTerms.waste.confirm.deleteItemTitle
              : kitchenTerms.waste.confirm.deleteBatchTitle
          }
          message={
            confirmAction.type === 'deleteItem'
              ? kitchenTerms.waste.confirm.deleteItemMessage(confirmAction.label)
              : kitchenTerms.waste.confirm.deleteBatchMessage
          }
          confirmLabel={kitchenTerms.waste.confirm.delete}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      <PinKeypadModal
        open={pinMode === 'submit'}
        title={kitchenTerms.waste.pin.submit.title}
        description={kitchenTerms.waste.pin.submit.description}
        confirmLabel={kitchenTerms.waste.pin.submit.confirm}
        onClose={() => setPinMode(null)}
        onConfirm={confirmSubmitToHo}
      />

      <PinKeypadModal
        open={pinMode === 'execute'}
        title={kitchenTerms.waste.pin.execute.title}
        description={kitchenTerms.waste.pin.execute.description}
        confirmLabel={kitchenTerms.waste.pin.execute.confirm}
        onClose={() => setPinMode(null)}
        onConfirm={confirmExecute}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
